import { useEffect, useMemo, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertTriangle, IndianRupee, Users, Wallet, MessageCircle, Download, Copy,
  Search, ChevronDown, ChevronUp, ExternalLink, Mail, Send, FileText,
} from "lucide-react";
import { toast } from "sonner";

function inr(n) {
  return `₹${(Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function agingBadge(days) {
  if (days <= 0) return { label: "Current", cls: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300" };
  if (days <= 30) return { label: `${days}d`, cls: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300" };
  if (days <= 60) return { label: `${days}d`, cls: "bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300" };
  if (days <= 90) return { label: `${days}d`, cls: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300" };
  return { label: `${days}d`, cls: "bg-red-600 text-white" };
}

export default function Outstanding() {
  const [data, setData] = useState({ rows: [], totals: null });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [bucketFilter, setBucketFilter] = useState("all");
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState({}); // client_id -> true
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkResults, setBulkResults] = useState([]); // [{client_name, message, whatsapp_url}]
  const [bulkLoading, setBulkLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/outstanding");
      setData(data);
    } catch (err) {
      toast.error("Failed to load outstanding");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.rows.filter(r => {
      if (q && !(r.client_name?.toLowerCase().includes(q) || r.client_code?.toLowerCase().includes(q))) return false;
      if (bucketFilter !== "all") {
        if (bucketFilter === "over_90" && r.buckets.over_90 <= 0) return false;
        if (bucketFilter === "d61_90" && r.buckets.d61_90 <= 0) return false;
        if (bucketFilter === "d31_60" && r.buckets.d31_60 <= 0) return false;
        if (bucketFilter === "d1_30" && r.buckets.d1_30 <= 0) return false;
        if (bucketFilter === "current" && r.buckets.current <= 0) return false;
      }
      return true;
    });
  }, [data.rows, search, bucketFilter]);

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const selectedTotal = filtered.reduce((s, r) => s + (selected[r.client_id] ? r.total_outstanding : 0), 0);

  function toggleAll() {
    if (selectedCount === filtered.length && filtered.length > 0) {
      setSelected({});
    } else {
      const map = {};
      filtered.forEach(r => { map[r.client_id] = true; });
      setSelected(map);
    }
  }

  async function sendReminderOne(clientId) {
    try {
      const { data } = await api.post("/billing/outstanding/reminder-message", { client_id: clientId });
      if (!data.whatsapp_url) {
        toast.error(`${data.client_name}: no mobile on file`);
        return;
      }
      window.open(data.whatsapp_url, "_blank", "noopener");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }

  async function copyReminder(clientId) {
    try {
      const { data } = await api.post("/billing/outstanding/reminder-message", { client_id: clientId });
      navigator.clipboard.writeText(data.message);
      toast.success(`Reminder for ${data.client_name} copied`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }

  async function emailReminder(clientId) {
    try {
      const { data } = await api.post("/billing/outstanding/reminder-message", { client_id: clientId });
      if (!data.email) { toast.error(`${data.client_name}: no email on file`); return; }
      const href = `mailto:${data.email}?subject=${encodeURIComponent(data.email_subject)}&body=${encodeURIComponent(data.email_body)}`;
      window.location.href = href;
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }

  async function openBulk() {
    const ids = Object.keys(selected).filter(k => selected[k]);
    if (ids.length === 0) { toast.error("Select at least one client"); return; }
    setBulkLoading(true);
    setBulkOpen(true);
    try {
      const results = await Promise.all(ids.map(async (cid) => {
        try {
          const { data } = await api.post("/billing/outstanding/reminder-message", { client_id: cid });
          return { ...data, client_id: cid, ok: true };
        } catch (err) {
          const row = data.rows.find(r => r.client_id === cid);
          return { client_id: cid, ok: false, client_name: row?.client_name || "Unknown", error: err.response?.data?.detail || "Failed" };
        }
      }));
      setBulkResults(results);
    } finally { setBulkLoading(false); }
  }

  function exportCSV() {
    const header = ["Client Code", "Client Name", "Mobile", "Email", "Outstanding (INR)", "Invoices", "Oldest Overdue (days)", "Current", "1-30", "31-60", "61-90", ">90"];
    const rows = filtered.map(r => [
      r.client_code || "", r.client_name || "", r.mobile || "", r.email || "",
      r.total_outstanding, r.invoice_count, r.oldest_days_overdue,
      r.buckets.current, r.buckets.d1_30, r.buckets.d31_60, r.buckets.d61_90, r.buckets.over_90,
    ]);
    const csv = [header, ...rows].map(r => r.map(v => {
      const s = String(v ?? "");
      return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `outstanding_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const totals = data.totals || {};

  return (
    <div className="space-y-5" data-testid="outstanding-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <Wallet className="w-3 h-3" /> Accounts receivable
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">All Outstanding</h1>
          <div className="text-sm text-muted-foreground mt-1">
            Every client with a pending balance — aged, sorted, and one click to remind.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="export-outstanding-btn" variant="outline" onClick={exportCSV}>
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
          <Button
            data-testid="bulk-remind-btn"
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={openBulk}
            disabled={selectedCount === 0}
          >
            <Send className="w-4 h-4 mr-1.5" />
            {selectedCount > 0 ? `Remind ${selectedCount} clients · ${inr(selectedTotal)}` : "Bulk remind"}
          </Button>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card data-testid="kpi-grand-total" className="p-4 border-l-[3px] border-red-500/70 col-span-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Grand Total Outstanding</div>
          <div className="font-mono font-semibold text-2xl mt-2 tabular-nums">{inr(totals.grand_total)}</div>
          <div className="flex items-center gap-3 text-[11px] mt-1 text-muted-foreground">
            <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {totals.client_count || 0} clients</span>
            <span className="flex items-center gap-1"><IndianRupee className="w-3 h-3" /> {totals.invoice_count || 0} invoices</span>
          </div>
        </Card>
        {[
          { key: "current", label: "Current", val: totals.current, cls: "border-slate-400/60" },
          { key: "d1_30", label: "1–30 days", val: totals.d1_30, cls: "border-amber-400/70" },
          { key: "d31_60", label: "31–60 days", val: totals.d31_60, cls: "border-orange-500/70" },
          { key: "d61_90", label: "61–90 days", val: totals.d61_90, cls: "border-red-500/70" },
          { key: "over_90", label: "> 90 days", val: totals.over_90, cls: "border-red-700/80" },
        ].map(b => (
          <button
            key={b.key}
            data-testid={`bucket-${b.key}`}
            onClick={() => setBucketFilter(bucketFilter === b.key ? "all" : b.key)}
            className={`text-left ${bucketFilter === b.key ? "ring-2 ring-blue-500 rounded-lg" : ""}`}
          >
            <Card className={`p-4 border-l-[3px] ${b.cls} hover:shadow-md transition-shadow`}>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{b.label}</div>
              <div className="font-mono font-semibold text-lg mt-2 tabular-nums">{inr(b.val)}</div>
            </Card>
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <Card className="p-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="search-outstanding"
            placeholder="Search client name or code…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-8"
          />
        </div>
        {bucketFilter !== "all" && (
          <Badge variant="outline" className="gap-1">
            Filter: {bucketFilter}
            <button onClick={() => setBucketFilter("all")} className="ml-1 hover:text-red-600">×</button>
          </Badge>
        )}
        <div className="text-xs text-muted-foreground font-mono tabular-nums ml-auto">
          {filtered.length} / {data.rows.length} clients
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <Table className="stripe-hover">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  data-testid="select-all-outstanding"
                  checked={filtered.length > 0 && selectedCount === filtered.length}
                  onCheckedChange={toggleAll}
                />
              </TableHead>
              <TableHead>Client</TableHead>
              <TableHead className="text-right w-24">Invoices</TableHead>
              <TableHead className="text-right w-28">Oldest</TableHead>
              <TableHead className="text-right w-28">Current</TableHead>
              <TableHead className="text-right w-28">1–30</TableHead>
              <TableHead className="text-right w-28">31–60</TableHead>
              <TableHead className="text-right w-28">61–90</TableHead>
              <TableHead className="text-right w-28">&gt; 90</TableHead>
              <TableHead className="text-right w-32">Outstanding</TableHead>
              <TableHead className="w-60">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={11} className="text-center py-10 text-sm text-muted-foreground">Loading…</TableCell></TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-10 text-sm text-muted-foreground">
                  <Wallet className="w-6 h-6 mx-auto mb-2 opacity-40" />
                  {data.rows.length === 0 ? "No outstanding balances — you're all collected!" : "No clients match this filter."}
                </TableCell>
              </TableRow>
            )}
            {filtered.map(r => {
              const isOpen = !!expanded[r.client_id];
              const badge = agingBadge(r.oldest_days_overdue);
              return (
                <Fragment key={r.client_id}>
                  <TableRow data-testid={`outstanding-row-${r.client_id}`} className="border-b">
                    <TableCell>
                      <Checkbox
                        data-testid={`select-${r.client_id}`}
                        checked={!!selected[r.client_id]}
                        onCheckedChange={(v) => setSelected(s => ({ ...s, [r.client_id]: !!v }))}
                      />
                    </TableCell>
                    <TableCell>
                      <button
                        className="flex items-center gap-2 text-left group"
                        onClick={() => setExpanded(e => ({ ...e, [r.client_id]: !e[r.client_id] }))}
                      >
                        {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        <div>
                          <div className="text-sm font-medium group-hover:text-blue-600 transition-colors">{r.client_name}</div>
                          <div className="text-[10px] text-muted-foreground font-mono">
                            {r.client_code} {r.mobile && `· ${r.mobile}`}
                          </div>
                        </div>
                      </button>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs tabular-nums">{r.invoice_count}</TableCell>
                    <TableCell className="text-right">
                      <Badge className={`${badge.cls} border-0 text-[10px] font-mono`}>{badge.label}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs tabular-nums text-muted-foreground">
                      {r.buckets.current ? inr(r.buckets.current) : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs tabular-nums text-amber-700 dark:text-amber-400">
                      {r.buckets.d1_30 ? inr(r.buckets.d1_30) : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs tabular-nums text-orange-700 dark:text-orange-400">
                      {r.buckets.d31_60 ? inr(r.buckets.d31_60) : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs tabular-nums text-red-700 dark:text-red-400">
                      {r.buckets.d61_90 ? inr(r.buckets.d61_90) : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs tabular-nums text-red-800 dark:text-red-300 font-semibold">
                      {r.buckets.over_90 ? inr(r.buckets.over_90) : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm font-bold tabular-nums">{inr(r.total_outstanding)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          data-testid={`whatsapp-${r.client_id}`}
                          size="icon" variant="ghost"
                          className="h-7 w-7 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950"
                          onClick={() => sendReminderOne(r.client_id)}
                          title={r.mobile ? "WhatsApp reminder" : "No mobile on file"}
                          disabled={!r.mobile}
                        >
                          <MessageCircle className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon" variant="ghost"
                          className="h-7 w-7 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"
                          onClick={() => emailReminder(r.client_id)}
                          title={r.email ? "Email reminder" : "No email on file"}
                          disabled={!r.email}
                        >
                          <Mail className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon" variant="ghost" className="h-7 w-7"
                          onClick={() => copyReminder(r.client_id)}
                          title="Copy reminder text"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" asChild title="Open client ledger">
                          <Link to={`/clients/${r.client_id}`}>
                            <ExternalLink className="w-4 h-4" />
                          </Link>
                        </Button>
                        <Button
                          data-testid={`statement-pdf-${r.client_id}`}
                          size="icon" variant="ghost"
                          className="h-7 w-7 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-zinc-800"
                          asChild
                          title="Download Statement of Account (PDF)"
                        >
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/billing/outstanding/${r.client_id}/statement.pdf`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <FileText className="w-4 h-4" />
                          </a>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow className="bg-slate-50/60 dark:bg-zinc-900/40">
                      <TableCell colSpan={11} className="p-0">
                        <div className="p-4 space-y-1">
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                            Pending invoices ({r.invoices.length})
                          </div>
                          <div className="divide-y divide-border rounded border border-border bg-background">
                            {r.invoices.map(inv => {
                              const b = agingBadge(inv.days_overdue);
                              return (
                                <div key={inv.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                                  <span className="font-mono text-xs w-24">{inv.number}</span>
                                  <span className="text-xs text-muted-foreground w-24">Issued {inv.issue_date || "—"}</span>
                                  <span className="text-xs text-muted-foreground w-24">Due {inv.due_date || "—"}</span>
                                  <Badge className={`${b.cls} border-0 text-[10px] font-mono`}>{b.label}</Badge>
                                  <span className="ml-auto font-mono tabular-nums text-xs">Total {inr(inv.total_amount)}</span>
                                  <span className="font-mono tabular-nums text-xs text-emerald-600">Paid {inr(inv.paid_amount)}</span>
                                  <span className="font-mono tabular-nums text-sm font-semibold text-red-600 w-28 text-right">{inr(inv.balance_amount)}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Bulk Reminders Dialog */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o) { setBulkOpen(false); setBulkResults([]); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-emerald-600" /> Send bulk reminders
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Review each message below and open WhatsApp with one click. Emergent doesn&apos;t auto-send — you stay in control.
            </div>
            {bulkLoading && <div className="text-center py-6 text-sm text-muted-foreground">Preparing reminders…</div>}
            {!bulkLoading && bulkResults.map((r) => (
              <Card key={r.client_id} className="p-3 space-y-2" data-testid={`bulk-result-${r.client_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium text-sm">{r.client_name}</div>
                  {r.ok ? (
                    <Badge variant="outline" className="text-[10px]">₹{Number(r.total).toLocaleString("en-IN")}</Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">Failed: {r.error}</Badge>
                  )}
                  {r.ok && !r.whatsapp_url && (
                    <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">
                      <AlertTriangle className="w-3 h-3 mr-1" /> No mobile
                    </Badge>
                  )}
                  <div className="ml-auto flex items-center gap-1.5">
                    {r.ok && r.whatsapp_url && (
                      <Button
                        data-testid={`bulk-wa-${r.client_id}`}
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 h-7"
                        asChild
                      >
                        <a href={r.whatsapp_url} target="_blank" rel="noreferrer">
                          <MessageCircle className="w-3.5 h-3.5 mr-1" /> Open WhatsApp
                        </a>
                      </Button>
                    )}
                    {r.ok && r.email && (
                      <Button
                        size="sm" variant="outline" className="h-7"
                        asChild
                      >
                        <a href={`mailto:${r.email}?subject=${encodeURIComponent(r.email_subject)}&body=${encodeURIComponent(r.email_body)}`}>
                          <Mail className="w-3.5 h-3.5 mr-1" /> Email
                        </a>
                      </Button>
                    )}
                    {r.ok && (
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { navigator.clipboard.writeText(r.message); toast.success("Copied"); }}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {r.ok && (
                      <Button
                        data-testid={`bulk-pdf-${r.client_id}`}
                        size="sm"
                        variant="outline"
                        className="h-7"
                        asChild
                      >
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/billing/outstanding/${r.client_id}/statement.pdf`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <FileText className="w-3.5 h-3.5 mr-1" /> Statement PDF
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
                {r.ok && <Textarea readOnly rows={4} value={r.message} className="font-mono text-[11px]" />}
              </Card>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBulkOpen(false); setBulkResults([]); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
